import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { securityKeys } from "../../enums/SecurityKeys.ENUM";
import { hasPermission } from "../../utils/authentication/authorizeNavigation";
import { buildPageContext } from "./copilotUtils";
import { useCopilotThreads } from "./useCopilotThreads";
import { useCopilotContextDisplay } from "./useCopilotContextDisplay";

const CopilotShellContext = createContext(null);

const isAdminDebugWorkspace = (location) => {
  const path = location?.pathname || "";
  if (!path.includes("recruitment-ai-assistant")) return false;
  const params = new URLSearchParams(location?.search || "");
  return params.get("debug") === "1";
};

const AUDIENCE_STORAGE_KEY = "molassist.audienceProfile";
const AUDIENCE_RECRUITER = "Recruiter";
const AUDIENCE_HIRING_MANAGER = "HiringManager";

const resolveDefaultAudience = () => {
  // Presentation default only — never grants access (OQ-03 Option A).
  if (hasPermission(securityKeys.LIST_APPLICANT) || hasPermission(securityKeys.LIST_TALENT_CRM)) {
    return AUDIENCE_RECRUITER;
  }
  return AUDIENCE_HIRING_MANAGER;
};

const readStoredAudience = () => {
  try {
    const stored = sessionStorage.getItem(AUDIENCE_STORAGE_KEY);
    if (stored === AUDIENCE_RECRUITER || stored === AUDIENCE_HIRING_MANAGER) return stored;
  } catch {
    /* ignore */
  }
  return resolveDefaultAudience();
};

export const CopilotShellProvider = ({ children }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [audienceProfile, setAudienceProfileState] = useState(readStoredAudience);
  const location = useLocation();

  const rawContext = useMemo(
    () => buildPageContext(location),
    [location.pathname, location.search]
  );
  const pageContext = useCopilotContextDisplay(rawContext);

  const threadApi = useCopilotThreads(pageContext);
  const canUseDebug = hasPermission(securityKeys.AI_CONTROL_CENTER);
  const debugMode = canUseDebug && isAdminDebugWorkspace(location);

  const openDrawer = useCallback(() => setDrawerVisible(true), []);
  const closeDrawer = useCallback(() => setDrawerVisible(false), []);
  const toggleDrawer = useCallback(() => setDrawerVisible((v) => !v), []);

  const setAudienceProfile = useCallback((value) => {
    const next =
      value === AUDIENCE_HIRING_MANAGER ? AUDIENCE_HIRING_MANAGER : AUDIENCE_RECRUITER;
    setAudienceProfileState(next);
    try {
      sessionStorage.setItem(AUDIENCE_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!threadApi.activeThreadId && threadApi.threads.length === 0) {
      threadApi.createThread();
    }
  }, [threadApi.activeThreadId, threadApi.threads.length, threadApi.createThread]);

  const value = useMemo(
    () => ({
      drawerVisible,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      pageContext,
      threads: threadApi,
      canUseDebug,
      debugMode,
      audienceProfile,
      setAudienceProfile,
      audienceOptions: [
        { label: "Recruiter", value: AUDIENCE_RECRUITER },
        { label: "Hiring manager", value: AUDIENCE_HIRING_MANAGER },
      ],
    }),
    [
      drawerVisible,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      pageContext,
      threadApi,
      canUseDebug,
      debugMode,
      audienceProfile,
      setAudienceProfile,
    ]
  );

  return (
    <CopilotShellContext.Provider value={value}>
      {children}
    </CopilotShellContext.Provider>
  );
};

export const useCopilotShell = () => {
  const ctx = useContext(CopilotShellContext);
  if (!ctx) {
    throw new Error("useCopilotShell must be used within CopilotShellProvider");
  }
  return ctx;
};

export default CopilotShellContext;
